import { Component, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';

import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import {
  currencyDisplayName,
  formatExchangeRateDate,
} from '@core/services/display-currency/convert-display-currency.util';
import { DisplayCurrencyService } from '@core/services/display-currency/display-currency.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';

export type KioskCurrencyPickerModalConfig = ModalConfig<{
  handle: typeof KioskCurrencyPickerModalComponent.handle;
}>;

@Component({
  selector: 'app-kiosk-currency-picker-modal',
  templateUrl: './kiosk-currency-picker-modal.component.html',
  standalone: false,
})
export class KioskCurrencyPickerModalComponent implements GetModalInterface<KioskCurrencyPickerModalConfig> {
  static handle = 'kiosk-currency-picker-modal' as const;

  protected readonly displayCurrency = inject(DisplayCurrencyService);
  private readonly marketplaceSelector = inject(MarketplaceStoreSelector);
  private readonly translateService = inject(TranslateService);
  private readonly modalsService = inject(ModalsService);
  private readonly modalSelector = inject(ModalStoreSelector);

  index$: Observable<number> = this.modalSelector
    .selectModal(KioskCurrencyPickerModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  protected baseCurrencyCode(): string {
    return (this.marketplaceSelector.selectMarketplace().currency ?? 'EUR').toUpperCase();
  }

  protected baseCurrencyName(): string {
    return currencyDisplayName(this.baseCurrencyCode(), this.locale());
  }

  protected rateDateLabel(): string {
    const validFrom = this.displayCurrency.ratesValidFrom();
    return validFrom ? formatExchangeRateDate(validFrom, this.locale()) : '';
  }

  protected isSelected(code: string | null): boolean {
    const selected = this.displayCurrency.selectedRate()?.code ?? null;
    return selected === code;
  }

  protected select(code: string | null): void {
    this.displayCurrency.select(code);
    this.close();
  }

  protected close(): void {
    this.modalsService.close(KioskCurrencyPickerModalComponent.handle);
  }

  private locale(): string {
    return this.translateService.currentLang || 'en';
  }
}
